import { z } from "zod";

/** Enumerations mirrored from the Luyoa OpenAPI schema. */
export const SEGMENT_IDS = ["champions", "regulars", "occasional", "new", "at_risk", "dormant"] as const;
export const TIERS = ["bronce", "plata", "oro"] as const;
export const CHANNELS = ["push", "whatsapp", "email"] as const;
export const OFFER_TYPES = ["percent_off", "two_for_one", "free_item", "points_multiplier"] as const;

export type SegmentId = (typeof SEGMENT_IDS)[number];
export type Channel = (typeof CHANNELS)[number];
export type OfferType = (typeof OFFER_TYPES)[number];

/** Max message length per channel (Luyoa API rule D10). */
export const MESSAGE_LIMITS: Record<Channel, number> = { push: 140, whatsapp: 1024, email: 5000 };

export const CHANNEL_LABELS: Record<Channel, string> = {
  push: "Notificación push",
  whatsapp: "WhatsApp",
  email: "Email",
};

export const SEGMENT_LABELS: Record<SegmentId, string> = {
  champions: "Campeones",
  regulars: "Regulares",
  occasional: "Ocasionales",
  new: "Nuevos",
  at_risk: "En riesgo",
  dormant: "Dormidos",
};

export const OFFER_LABELS: Record<OfferType, string> = {
  percent_off: "Descuento %",
  two_for_one: "2x1",
  free_item: "Producto gratis",
  points_multiplier: "Puntos multiplicados",
};

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Usa formato YYYY-MM-DD");

export const audienceSchema = z
  .object({
    segment_id: z.enum(SEGMENT_IDS).optional(),
    home_location_id: z.string().optional(),
    tier: z.enum(TIERS).optional(),
    inactive_days_min: z.number().int().min(0).optional(),
    min_visits: z.number().int().min(0).optional(),
  })
  .strict();
export type Audience = z.infer<typeof audienceSchema>;

export const offerSchema = z
  .object({
    type: z.enum(OFFER_TYPES),
    value: z.number().positive().optional(),
    product_id: z.string().optional(),
    valid_from: isoDate,
    valid_to: isoDate,
  })
  .strict();
export type Offer = z.infer<typeof offerSchema>;

export const campaignDraftInputSchema = z
  .object({
    name: z.string().min(3).max(80),
    objective: z.string().min(3).max(300),
    channel: z.enum(CHANNELS),
    audience: audienceSchema,
    offer: offerSchema,
    message: z.string().min(1),
  })
  .strict();
export type CampaignDraftInput = z.infer<typeof campaignDraftInputSchema>;

export interface CampaignValidationContext {
  /** The dataset's "today" (YYYY-MM-DD). Offers must start after it. */
  dataAsOf: string;
  productIds: ReadonlySet<string>;
  locationIds: ReadonlySet<string>;
}

/**
 * Business rules the Luyoa API enforces (plus a few we add), checked locally so the
 * model gets precise, fixable feedback before we call the API.
 * Returns a list of human-readable problems in Spanish (empty = valid).
 */
export function validateCampaignDraft(input: CampaignDraftInput, ctx: CampaignValidationContext): string[] {
  const problems: string[] = [];
  const limit = MESSAGE_LIMITS[input.channel];
  const length = [...input.message].length;
  if (length > limit) {
    problems.push(`El mensaje tiene ${length} caracteres y el límite para ${input.channel} es ${limit}.`);
  }

  const { offer } = input;
  if ((offer.type === "percent_off" || offer.type === "points_multiplier") && offer.value === undefined) {
    problems.push(`La oferta ${offer.type} requiere "value".`);
  }
  if (offer.type === "percent_off" && offer.value !== undefined && offer.value > 100) {
    problems.push("Un descuento no puede ser mayor a 100%.");
  }
  if ((offer.type === "two_for_one" || offer.type === "free_item") && !offer.product_id) {
    problems.push(`La oferta ${offer.type} requiere "product_id".`);
  }
  if (offer.product_id && !ctx.productIds.has(offer.product_id)) {
    problems.push(`El producto ${offer.product_id} no existe en el catálogo. Usa uno de: ${[...ctx.productIds].join(", ")}.`);
  }
  if (offer.valid_from <= ctx.dataAsOf) {
    problems.push(`La oferta debe iniciar después de la fecha de corte (${ctx.dataAsOf}).`);
  }
  if (offer.valid_to < offer.valid_from) {
    problems.push("valid_to debe ser igual o posterior a valid_from.");
  }

  const { audience } = input;
  if (audience.home_location_id && !ctx.locationIds.has(audience.home_location_id)) {
    problems.push(`La sucursal ${audience.home_location_id} no existe. Usa una de: ${[...ctx.locationIds].join(", ")}.`);
  }
  if (Object.values(audience).every((v) => v === undefined)) {
    problems.push("Define al menos un criterio de audiencia (no enviamos a todos los clientes por accidente).");
  }
  return problems;
}

/** Lift in percentage points between treatment and control visit rates (D12). */
export function computeLift(treatment: number | null | undefined, control: number | null | undefined): number | null {
  if (treatment == null || control == null) return null;
  return Math.round((treatment - control) * 10000) / 100;
}

export type LocalCampaignStatus = "draft" | "sending" | "sent" | "failed";
